import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Typography,
  DatePicker,
  Tooltip,
} from 'antd';
import { PlusOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  PublishedEventType,
  SIGNALR_MATCH_ALL_DIVISIONS,
  StatusIcon,
  useSignalREvent,
} from 'app/helpers/signalRHelper';

import ProductStatusFilter from 'app/shared/components/ProductStatusFilter';
import type { ProductStatusFilterValue } from 'app/shared/components/productStatusFilterUtils';
import {
  fetchTaxRules,
  setFilterName,
  setFilterCentre,
  setFilterCountry,
  setFilterSpecificCentre,
  setFilterStartDateFrom,
  type TaxFilterCentre,
  type TaxFilterCountry,
} from './actions';
import {
  getTaxLoading,
  getTaxFilterName,
  getTaxFilterCentre,
  getTaxFilterCountry,
  getTaxFilterSpecificCentre,
  getTaxFilterStartDateFrom,
  getTaxList,
  getFilteredTaxRows,
} from './selectors';
import '../CommissionManager/Page.css';
import { getAllCountries } from 'app/shared/api/getCountries';
import {
  extractCentreArrayPayload,
  getCentres,
  getCentresInDetails,
  mergeCentreNamesIntoLookup,
  mergeRawCentreRowsIntoLookup,
  centreOptionValuesEqual,
  centreSelectOptionValue,
  isCentreListItemActive,
  positiveNumericCentreId,
  type CentreListItem,
} from 'app/shared/api/getCentres';
import type { CountryWithNumericId } from 'app/shared/api/getCountries';
import type { TaxRuleListRow } from 'app/shared/api/taxRules/types';
import DateHelper from 'app/helpers/dateHelper';
import ScopeFilterRow from 'app/shared/components/ScopeFilterRow';
import ManagerSearchByName from 'app/shared/components/ManagerSearchByName';

import './TaxManager.css';

const { Title } = Typography;

type TableRow = TaxRuleListRow & { scopeLabels: string[] };

function normalizeTaxCentreFilterId(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.trunc(n);
}

function findCentreListItemById(centres: CentreListItem[], rid: number): CentreListItem | undefined {
  for (const x of centres) {
    const pid = positiveNumericCentreId(x);
    if (pid != null && pid === rid) return x;
    const idNum = Number(String(x.id).trim());
    if (Number.isFinite(idNum) && Math.trunc(idNum) === rid) return x;
  }
  return undefined;
}

function buildScopeLabels(
  row: TaxRuleListRow,
  countries: CountryWithNumericId[],
  centres: CentreListItem[],
  centreNameById: Map<number, string>
): string[] {
  if (row.scopeLabelsFromApi?.length) return row.scopeLabelsFromApi;
  if (row.centreId != null && row.centreId > 0) {
    const rid = normalizeTaxCentreFilterId(row.centreId);
    if (rid == null) return [];
    const fromLookup = centreNameById.get(rid);
    if (fromLookup) return [fromLookup];
    const c = findCentreListItemById(centres, rid);
    return c?.name?.trim() ? [c.name.trim()] : [];
  }
  if (row.countryId != null) {
    if (typeof row.countryId === 'number' && row.countryId > 0) {
      const id = row.countryId;
      const co = countries.find(
        (x) => x.numericId === id || (Number.isFinite(Number(x.id)) && Number(x.id) === id)
      );
      return co?.name ? [co.name] : [];
    }
    const code = String(row.countryId).trim();
    if (code !== '') {
      const co = countries.find((x) => String(x.id).toUpperCase() === code.toUpperCase());
      return co?.name ? [co.name] : [];
    }
  }
  return [];
}

function formatLastModified(row: TaxRuleListRow): string {
  const s = row.lastModifiedBySurname?.trim();
  const g = row.lastModifiedByGivenName?.trim();
  if (s && g) return `${s} ${g}`;
  if (row.lastModifiedBy?.trim()) return row.lastModifiedBy.trim();
  return '—';
}

function countryKeyForTaxRow(row: TaxRuleListRow): string | null {
  if (row.countryId == null) return null;
  if (typeof row.countryId === 'number') return `n:${row.countryId}`;
  const s = String(row.countryId).trim();
  return s === '' ? null : `s:${s.toUpperCase()}`;
}

function displayLabelForCentreFilterOption(
  row: TaxRuleListRow,
  centreRow: CentreListItem | undefined,
  countries: CountryWithNumericId[],
  centres: CentreListItem[],
  centreNameById: Map<number, string>
): string {
  if (centreRow?.name?.trim()) return centreRow.name.trim();
  const rid = normalizeTaxCentreFilterId(row.centreId);
  if (rid != null) {
    const fromLookup = centreNameById.get(rid);
    if (fromLookup?.trim()) return fromLookup.trim();
  }
  const labels = buildScopeLabels(row, countries, centres, centreNameById);
  if (labels.length) return labels.join(', ');
  return '';
}

function buildCentreFilterOptionsFromTaxes(
  taxRows: TaxRuleListRow[],
  centres: CentreListItem[],
  countries: CountryWithNumericId[],
  centreNameById: Map<number, string>
): { value: number | string; label: string }[] {
  const seen = new Set<string>();
  const rows: { value: number | string; label: string }[] = [];

  const pushOption = (value: number | string, label: string) => {
    const dedupe = `${typeof value}\0${value}`;
    if (seen.has(dedupe)) return;
    if (!label.trim()) return;
    seen.add(dedupe);
    rows.push({ value, label });
  };

  const usedCentreIds = new Set<number>();
  for (const row of taxRows) {
    const rid = normalizeTaxCentreFilterId(row.centreId);
    if (rid != null && rid > 0) usedCentreIds.add(rid);
  }

  for (const id of usedCentreIds) {
    const rowForLabel = taxRows.find((r) => normalizeTaxCentreFilterId(r.centreId) === id);
    if (!rowForLabel) continue;
    const c = findCentreListItemById(centres, id);
    const label = displayLabelForCentreFilterOption(
      rowForLabel,
      c,
      countries,
      centres,
      centreNameById
    );
    if (!label) continue;

    if (c && isCentreListItemActive(c)) {
      const v = centreSelectOptionValue(c);
      if (v != null) pushOption(v, label);
    } else {
      pushOption(id, label);
    }
  }

  rows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return rows;
}

function resolveCountryLabel(
  countries: CountryWithNumericId[],
  countryId?: number,
  countryCode?: string
): string {
  if (countryId != null && countryId > 0) {
    const co = countries.find(
      (x) =>
        x.numericId === countryId ||
        (Number.isFinite(Number(x.id)) && Number(x.id) === countryId)
    );
    if (co?.name) return co.name;
  }
  if (countryCode != null) {
    const code = String(countryCode).trim().toUpperCase();
    const co = countries.find((x) => String(x.id).toUpperCase() === code);
    if (co?.name) return co.name;
    return code;
  }
  return '';
}

function buildCountryFilterOptionsFromTaxes(
  taxRows: TaxRuleListRow[],
  countries: CountryWithNumericId[],
  centres: CentreListItem[]
): { value: string; label: string }[] {
  const seen = new Set<string>();
  const rows: { value: string; label: string }[] = [];

  const addCountry = (countryId?: number, countryCode?: string) => {
    const key =
      countryId != null && countryId > 0
        ? `n:${countryId}`
        : countryCode != null
          ? `s:${String(countryCode).trim().toUpperCase()}`
          : null;
    if (key == null || seen.has(key)) return;
    const label = resolveCountryLabel(countries, countryId, countryCode);
    if (!label) return;
    seen.add(key);
    rows.push({ value: key, label });
  };

  for (const row of taxRows) {
    const rid = normalizeTaxCentreFilterId(row.centreId);
    if (rid != null && rid > 0) {
      const c = findCentreListItemById(centres, rid);
      if (c?.countryId != null && Number(c.countryId) > 0) {
        addCountry(Number(c.countryId), c.countryCode != null ? String(c.countryCode) : undefined);
      } else if (c?.countryCode) {
        addCountry(undefined, String(c.countryCode));
      }
    } else if (countryKeyForTaxRow(row) != null) {
      if (typeof row.countryId === 'number' && row.countryId > 0) {
        addCountry(row.countryId, undefined);
      } else if (row.countryId != null) {
        const s = String(row.countryId).trim();
        if (s) addCountry(undefined, s);
      }
    }
  }
  rows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return rows;
}

function parseCountryFilterValue(raw: string | null): TaxFilterCountry {
  if (raw == null || raw === '') return null;
  if (raw.startsWith('n:')) {
    const n = Number(raw.slice(2));
    if (Number.isFinite(n) && n > 0) return { countryId: Math.trunc(n) };
    return null;
  }
  if (raw.startsWith('s:')) {
    const code = raw.slice(2).trim();
    return code ? { countryCode: code } : null;
  }
  return null;
}

function ScopeCell({ labels }: { labels: string[] }) {
  if (!labels.length) return <span>—</span>;
  const max = 2;
  if (labels.length <= max) {
    return <span>{labels.join(', ')}</span>;
  }
  const shown = labels.slice(0, max).join(', ');
  const rest = labels.length - max;
  const full = labels.join(', ');
  return (
    <span>
      {shown},{' '}
      <Tooltip title={full}>
        <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
          +{rest} more
        </Button>
      </Tooltip>
    </span>
  );
}

interface StateProps {
  loading: boolean;
  list: TaxRuleListRow[];
  filterName: string;
  filterCentre: TaxFilterCentre;
  filterCountry: TaxFilterCountry;
  filterSpecificCentre: boolean;
  filterStartDateFrom: string | null;
}

interface DispatchProps {
  fetchList: () => void;
  setFilterNameAction: (v: string) => void;
  setFilterCentreAction: (v: TaxFilterCentre) => void;
  setFilterCountryAction: (v: TaxFilterCountry) => void;
  setFilterSpecificCentreAction: (v: boolean) => void;
  setFilterStartDateFromAction: (v: string | null) => void;
}

type Props = StateProps & DispatchProps;

const TaxManagerPage: React.FC<Props> = ({
  loading,
  list,
  filterName,
  filterCentre,
  filterCountry,
  filterSpecificCentre,
  filterStartDateFrom,
  fetchList,
  setFilterNameAction,
  setFilterCentreAction,
  setFilterCountryAction,
  setFilterSpecificCentreAction,
  setFilterStartDateFromAction,
}) => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState<CountryWithNumericId[]>([]);
  const [centres, setCentres] = useState<CentreListItem[]>([]);
  const [centreNameById, setCentreNameById] = useState<Map<number, string>>(() => new Map());
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [activeStatusFilter, setActiveStatusFilter] = useState<ProductStatusFilterValue>('all');

  const refreshList = useCallback(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAllCountries(),
      getCentres(),
      getCentresInDetails().catch(() => null),
    ])
      .then(([c0, c1, detailRaw]) => {
        if (cancelled) return;
        setCountries(c0 ?? []);
        const list = Array.isArray(c1) ? c1 : [];
        setCentres(list);
        const lookup = new Map<number, string>();
        mergeCentreNamesIntoLookup(lookup, list);
        if (detailRaw != null) {
          const detailRows = Array.isArray(detailRaw)
            ? detailRaw
            : extractCentreArrayPayload(detailRaw);
          mergeRawCentreRowsIntoLookup(lookup, detailRows);
        }
        setCentreNameById(lookup);
      })
      .catch(() => {
        if (!cancelled) {
          setCountries([]);
          setCentres([]);
          setCentreNameById(new Map());
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { status, lastEventReceived } = useSignalREvent(
    [PublishedEventType.CommissionGroupEventPublished],
    refreshList,
    SIGNALR_MATCH_ALL_DIVISIONS
  );

  const centreFilterSelectValue = useMemo(() => {
    if (filterCentre?.id == null) return undefined;
    for (const c of centres) {
      const v = centreSelectOptionValue(c);
      if (v != null && centreOptionValuesEqual(v, filterCentre.id)) return v;
    }
    const orphan = normalizeTaxCentreFilterId(filterCentre.id);
    return orphan != null ? orphan : undefined;
  }, [centres, filterCentre]);

  const taxStateForSelect = useMemo(
    () => ({
      taxManager: {
        list,
        loading,
        filterName,
        filterCentre,
        filterCountry,
        filterSpecificCentre,
        filterStartDateFrom,
      },
    }),
    [
      list,
      loading,
      filterName,
      filterCentre,
      filterCountry,
      filterSpecificCentre,
      filterStartDateFrom,
    ]
  );

  const filtered = useMemo(
    () => getFilteredTaxRows(taxStateForSelect, { centres, activeStatusFilter }),
    [taxStateForSelect, centres, activeStatusFilter]
  );

  const tableData: TableRow[] = useMemo(
    () =>
      filtered.map((row) => ({
        ...row,
        scopeLabels: buildScopeLabels(row, countries, centres, centreNameById),
      })),
    [filtered, countries, centres, centreNameById]
  );

  const centreFilterOptions = useMemo(
    () => buildCentreFilterOptionsFromTaxes(list, centres, countries, centreNameById),
    [list, centres, countries, centreNameById]
  );

  const countryFilterOptions = useMemo(
    () => buildCountryFilterOptionsFromTaxes(list, countries, centres),
    [list, countries, centres]
  );

  const countryFilterSelectValue = useMemo(() => {
    if (filterCountry?.countryId != null && filterCountry.countryId > 0) {
      return `n:${filterCountry.countryId}`;
    }
    if (filterCountry?.countryCode != null) {
      return `s:${String(filterCountry.countryCode).trim().toUpperCase()}`;
    }
    return undefined;
  }, [filterCountry]);

  const handleAddNew = () => {
    navigate('/tax-manager/add');
  };

  const handleOpenRow = (record: TaxRuleListRow) => {
    navigate(`/tax-manager/${record.id}`);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 72,
      sorter: (a: TableRow, b: TableRow) => (a.id ?? 0) - (b.id ?? 0),
    },
    {
      title: 'Tax Name',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      sorter: (a: TableRow, b: TableRow) => (a.name ?? '').localeCompare(b.name ?? ''),
      render: (_: unknown, record: TableRow) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => handleOpenRow(record)}>
          {record.name}
        </Button>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 130,
      sorter: (a: TableRow, b: TableRow) => {
        const ta = a.startDate?.valueOf() ?? 0;
        const tb = b.startDate?.valueOf() ?? 0;
        return ta - tb;
      },
      render: (d: TableRow['startDate']) =>
        d && d.isValid() ? DateHelper.ToDateString(d.toDate(), DateHelper.DDMMMYYYY) : '—',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 130,
      sorter: (a: TableRow, b: TableRow) => {
        const ta = a.endDate?.valueOf() ?? 0;
        const tb = b.endDate?.valueOf() ?? 0;
        return ta - tb;
      },
      render: (d: TableRow['endDate']) =>
        d && d.isValid() ? DateHelper.ToDateString(d.toDate(), DateHelper.DDMMMYYYY) : '—',
    },
    {
      title: 'Country/Centre',
      key: 'countryCentre',
      width: 200,
      render: (_: unknown, record: TableRow) => <ScopeCell labels={record.scopeLabels} />,
    },
    {
      title: 'Last Modified By',
      key: 'lastModifiedBy',
      width: 160,
      render: (_: unknown, record: TableRow) => <span>{formatLastModified(record)}</span>,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center' as const,
      width: 88,
      render: (val: boolean | undefined) =>
        val !== false ? <span className="commission-active-yes">Yes</span> : <span className="commission-active-no">No</span>,
    },
  ];

  return (
    <>
      <Title level={3} style={{ margin: 0, marginBottom: 16 }}>Tax Manager</Title>

      <Space style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap' }}>
        <Space wrap>
          <ManagerSearchByName
            value={filterName}
            onChange={setFilterNameAction}
            disabled={!filtersCollapsed}
          />
          <ProductStatusFilter
            value={activeStatusFilter}
            onChange={setActiveStatusFilter}
            disabled={!filtersCollapsed}
          />
        </Space>

        <Space wrap>
          <StatusIcon status={status} lastEventReceived={lastEventReceived} />
          <Tooltip title={`${filtersCollapsed ? 'Show' : 'Hide'} filters`}>
            <Button
              type="primary"
              aria-label={`${filtersCollapsed ? 'Show' : 'Hide'} filters`}
              icon={<FilterOutlined />}
              onClick={() => setFiltersCollapsed((c) => !c)}
            />
          </Tooltip>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            Add New
          </Button>
        </Space>
      </Space>

      {!filtersCollapsed && (
        <ScopeFilterRow
          variant="country-centre"
          useSpecificScope={filterSpecificCentre}
          onUseSpecificScopeChange={(checked) => {
            setFilterSpecificCentreAction(checked);
            if (checked) {
              setFilterCountryAction(null);
            } else {
              setFilterCentreAction(null);
            }
          }}
          primaryOptions={countryFilterOptions}
          secondaryOptions={centreFilterOptions}
          primaryValue={countryFilterSelectValue}
          secondaryValue={centreFilterSelectValue}
          onPrimaryChange={(v) => {
            if (v == null || v === '') {
              setFilterCountryAction(null);
              return;
            }
            setFilterCountryAction(parseCountryFilterValue(String(v)));
          }}
          onSecondaryChange={(rawId) => {
            if (rawId == null || (typeof rawId === 'string' && rawId.trim() === '')) {
              setFilterCentreAction(null);
              return;
            }
            const c = centres.find((x) => {
              const optionValue = centreSelectOptionValue(x);
              return optionValue != null && centreOptionValuesEqual(optionValue, rawId);
            });
            if (!c) {
              const nid = normalizeTaxCentreFilterId(rawId);
              if (nid != null) {
                setFilterCentreAction({ id: nid });
              } else {
                setFilterCentreAction(null);
              }
              return;
            }
            const id = centreSelectOptionValue(c)!;
            setFilterCentreAction({
              id,
              countryId: c?.countryId != null ? Number(c.countryId) : undefined,
              countryCode: c?.countryCode != null ? String(c.countryCode).trim() : undefined,
            });
          }}
          trailing={
            <>
              <span className="scope-filter-label">Start Date From</span>
              <DatePicker
                format={DateHelper.DATE_PICKER_FORMATS}
                allowClear
                placeholder="Select Start Date"
                style={{ width: 180 }}
                value={filterStartDateFrom ? dayjs(filterStartDateFrom) : null}
                onChange={(d) => setFilterStartDateFromAction(d ? d.format('YYYY-MM-DD') : null)}
              />
            </>
          }
        />
      )}

      <Table<TableRow>
        rowKey="id"
        loading={loading}
        dataSource={tableData}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowClassName={(record) => (record.isActive === false ? 'inactive-row' : '')}
      />
    </>
  );
};

const mapStateToProps = (state: unknown): StateProps => ({
  loading: getTaxLoading(state as object),
  list: getTaxList(state as object),
  filterName: getTaxFilterName(state as object),
  filterCentre: getTaxFilterCentre(state as object),
  filterCountry: getTaxFilterCountry(state as object),
  filterSpecificCentre: getTaxFilterSpecificCentre(state as object),
  filterStartDateFrom: getTaxFilterStartDateFrom(state as object),
});

const mapDispatchToProps: DispatchProps = {
  fetchList: fetchTaxRules,
  setFilterNameAction: setFilterName,
  setFilterCentreAction: setFilterCentre,
  setFilterCountryAction: setFilterCountry,
  setFilterSpecificCentreAction: setFilterSpecificCentre,
  setFilterStartDateFromAction: setFilterStartDateFrom,
};

export default connect(mapStateToProps, mapDispatchToProps)(TaxManagerPage);